import { useContext } from 'react';
import { Context } from '../../../App';

const fmt = (v, digits = 2) => {
  if (v === null || v === undefined || v === '' || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(digits) : '—';
};

export default function PileRockBcSketch({
  // REQUIRED PROP NAMES (as provided)
  name,
  socketDiameter, // Br (m)
  socketLength, // Dr (m)
  c0, // rock UCS (MPa)
  rqd, // RQD (%)
  pileLength, // total cap-under to tip (m)
  gammaConcrete, // γ_conc (kN/m^3)
  sigmaConcrete, // f'c (MPa)
  nms, // N_ms (dimensionless)
  qsrOption, // q_sr option (string/label)
  FsSt, // Factor of Safety - Static
  FsSs, // Factor of Safety - Seismic
  FSt, // Static axial compression load (MN)
  FSs, // Seismic axial compression load (MN)
  FTension, // Seismic tension/uplift (MN)
  variant,
}) {
  const { theme: appTheme } = useContext(Context);

  // --- SVG canvas
  const VW = 820;
  const VH = 520;
  const margin = 20;
  const groundY = 50;
  const interfaceY = 300;
  const bottomY = VH - margin;

  // --- Superstructure geometry (kept)
  const capWidth = 220;
  const capHeight = 30;
  const capX = VW / 2 - capWidth / 2;
  const capY = groundY + 35;

  const columnWidth = 40;
  const columnHeight = 65;
  const columnX = VW / 2 - columnWidth / 2;
  const columnY = groundY - capHeight;

  // --- Piles geometry (kept)
  const pileWidth = 30;
  const pileTopY = capY + capHeight;
  const pileSpacing = 95;
  const pile1X = VW / 2 - pileSpacing / 2 - pileWidth / 2;
  const pile2X = VW / 2 + pileSpacing / 2 - pileWidth / 2;

  // --- Vertical scaling for depiction (not to scale)
  const pxPerM = 35; // drawing heuristic
  const socketDepthPxRaw = Number(socketLength) > 0 ? Number(socketLength) * pxPerM : 0;
  const socketDepthPx = Math.max(20, Math.min(120, socketDepthPxRaw || 0));
  const pileEmbedBottom = Math.min(bottomY - 20, interfaceY + (Number(socketLength) > 0 ? socketDepthPx : 0));

  // --- Placeholder logic
  const hasCore =
    !socketDiameter &&
    !socketLength &&
    !c0 &&
    !rqd &&
    !pileLength &&
    !gammaConcrete &&
    !sigmaConcrete &&
    !nms &&
    !qsrOption &&
    !FsSt &&
    !FsSs &&
    !FSt &&
    !FSs &&
    !FTension;

  const theme = variant === 'pdf-light' ? 'light' : appTheme;

  // --- Theme colors
  const lineColor = theme === 'light' ? '#222' : '#878787ff';
  const textColor = theme === 'light' ? '#222' : '#fff';
  const concreteFillColor = theme === 'light' ? '#fff' : '#282828ff';
  const soilFillColor = theme === 'light' ? '#999' : '#ffffffff';

  // --- Panel style
  const panelFill = theme === 'light' ? '#ffffff' : '#2c2c2cff';
  const panelStroke = theme === 'light' ? '#bdbdbd' : '#a8a8a8';
  const panelHeaderFontSize = 13;
  const panelRowFontSize = 12.5;

  // --- Total pile length (Lt) display uses provided pileLength
  const Lt_m = Number.isFinite(Number(pileLength)) ? Number(pileLength) : null;
  const dimLtX = pile2X + pileWidth + 36;
  const dimLtTopY = pileTopY;
  const dimLtBottomY = Number(socketLength) > 0 ? pileEmbedBottom : interfaceY; // draw to actual tip/interface in sketch

  // --- Info Panels layout
  const rockBox = { x: margin + 610, y: groundY + 10, w: 160, h: 100 }; // + space for Nms/qsr
  const concBox = { x: margin + 610, y: groundY + 120, w: 160, h: 80 };
  const loadsBox = { x: margin + 55, y: groundY + 10, w: 200, h: 100 };
  const fsBox = { x: margin + 55, y: groundY + 120, w: 160, h: 78 };

  return (
    <div className="sketch-rail">
      <div className="sketch-wrap">
        <svg
          viewBox={`0 0 ${VW} ${VH}`}
          width="100%"
          role="img"
          aria-label="Pile settlement sketch with total length, rock, concrete, loads, and safety factors"
          style={{ display: 'block', maxWidth: '980px', margin: '0 auto' }}
        >
          <defs>
            {/* Soil dot texture */}
            <pattern id="clayDots" patternUnits="userSpaceOnUse" width="8" height="8">
              <circle cx="1" cy="1" r="0.6" fill={soilFillColor} />
            </pattern>

            {/* Rock hatch */}
            <pattern id="rockHatch" patternUnits="userSpaceOnUse" width="25" height="25">
              <rect width="20" height="20" fill="none" />
              <path d="M0 12 L12 0 M4 30 L30 4 M-4 8 L8 -4" stroke="#777" strokeWidth="0.5" />
              <path d="M0 4 L12 30 M8 0 L30 8" stroke="#999" strokeWidth="0.3" />
            </pattern>

            {/* Arrow markers */}
            <marker id="arrow" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill={lineColor} />
            </marker>
            <marker id="arrowEnd" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto">
              <path d="M 0 0 L 10 5 L 0 10 z" fill={lineColor} />
            </marker>
            <marker
              id="arrowStart"
              viewBox="0 0 10 10"
              refX="5"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill={lineColor} />
            </marker>
          </defs>

          {/* Frame */}
          <line x1={margin} y1={groundY} x2={VW - margin} y2={groundY} stroke={lineColor} strokeWidth="1.2" />
          <text x={margin + 10} y={groundY - 10} fontSize="14" fill={textColor}>
            Ground Surface
          </text>
          <line x1={margin} y1={bottomY} x2={VW - margin} y2={bottomY} stroke={lineColor} strokeWidth="1.2" />
          <line x1={margin} y1={groundY} x2={margin} y2={bottomY} stroke={lineColor} strokeWidth="1.2" />
          <line x1={VW - margin} y1={groundY} x2={VW - margin} y2={bottomY} stroke={lineColor} strokeWidth="1.2" />

          {/* Soil */}
          <rect
            x={margin}
            y={groundY}
            width={VW - 2 * margin}
            height={interfaceY - groundY}
            fill="url(#clayDots)"
            opacity={hasCore ? 1 : 0.3}
          />
          <text x={margin + 10} y={groundY + 25} fontSize="16" fill={textColor}>
            Soil
          </text>

          {/* Rock */}
          <rect
            x={margin}
            y={interfaceY}
            width={VW - 2 * margin}
            height={bottomY - interfaceY}
            fill="url(#rockHatch)"
            opacity={hasCore ? 1 : 0.3}
          />
          <text x={margin + 10} y={interfaceY + 25} fontSize="16" fill={textColor}>
            Rock
          </text>

          {/* Interface */}
          <line x1={margin} y1={interfaceY} x2={VW - margin} y2={interfaceY} stroke={lineColor} strokeWidth="1" />

          {/* Column & cap */}
          <rect
            x={columnX}
            y={columnY}
            width={columnWidth}
            height={columnHeight}
            fill={concreteFillColor}
            stroke={lineColor}
            strokeWidth="1"
          />
          <rect
            x={capX}
            y={capY}
            width={capWidth}
            height={capHeight}
            fill={concreteFillColor}
            stroke={lineColor}
            strokeWidth="1"
          />

          {/* Two piles */}
          {[pile1X, pile2X].map((px, idx) => (
            <g key={idx}>
              {/* Through soil */}
              <rect
                x={px}
                y={pileTopY}
                width={pileWidth}
                height={Math.max(0, interfaceY - pileTopY)}
                fill={concreteFillColor}
                stroke={lineColor}
                strokeWidth="1"
              />
              {/* Socket in rock */}
              <rect
                x={px}
                y={interfaceY}
                width={pileWidth}
                height={Number(socketLength) > 0 ? Math.max(0, pileEmbedBottom - interfaceY) : 0}
                fill={concreteFillColor}
                stroke={lineColor}
                strokeWidth="1"
              />
              {/* Centerline (vertical, centered) */}
              <line
                x1={px + pileWidth / 2}
                y1={pileTopY + 10}
                x2={px + pileWidth / 2}
                y2={(Number(socketLength) > 0 ? pileEmbedBottom : interfaceY) - 10}
                stroke="#b44"
                strokeWidth="1"
                strokeDasharray="6 6"
                strokeLinecap="round"
                opacity="0.6"
              />
            </g>
          ))}

          {/* Dr (socketLength) */}
          {Number(socketLength) > 0 && (
            <g>
              <line
                x1={pile1X - 30}
                y1={interfaceY}
                x2={pile1X - 30}
                y2={pileEmbedBottom}
                stroke={lineColor}
                strokeWidth="1"
                strokeDasharray="5 4"
              />
              <line
                x1={pile1X - 30}
                y1={interfaceY + 3}
                x2={pile1X - 30}
                y2={pileEmbedBottom - 5}
                stroke={lineColor}
                strokeWidth="1.2"
                markerStart="url(#arrowStart)"
                markerEnd="url(#arrowEnd)"
              />
              <text
                x={pile1X - 85}
                y={(interfaceY + pileEmbedBottom) / 2 - 15}
                fill={textColor}
                transform={`rotate(-90 ${pile1X - 48},${(interfaceY + pileEmbedBottom) / 2})`}
              >
                {`Dr = ${fmt(socketLength, 2)} m`}
              </text>
              <text
                x={pile1X - 85}
                y={(interfaceY + pileEmbedBottom) / 2 + 5}
                fill={textColor}
                transform={`rotate(-90 ${pile1X - 48},${(interfaceY + pileEmbedBottom) / 2})`}
                fontSize="12"
              >
                (Socket Length)
              </text>
            </g>
          )}

          {/* Br (socketDiameter) at tip/interface */}
          {socketDiameter && (
            <g>
              <line
                x1={pile2X + 2}
                y1={(Number(socketLength) > 0 ? pileEmbedBottom : interfaceY) + 14}
                x2={pile2X + pileWidth - 2}
                y2={(Number(socketLength) > 0 ? pileEmbedBottom : interfaceY) + 14}
                stroke={lineColor}
                strokeWidth="1.2"
                markerStart="url(#arrowStart)"
                markerEnd="url(#arrowEnd)"
              />
              <text
                x={VW / 2 + 70}
                y={(Number(socketLength) > 0 ? pileEmbedBottom : interfaceY) + 18}
                fontSize="14"
                fill={textColor}
              >
                {`Br = ${fmt(socketDiameter, 2)} m`}
              </text>
              <text
                x={VW / 2 + 70}
                y={(Number(socketLength) > 0 ? pileEmbedBottom : interfaceY) + 38}
                fontSize="12"
                fill={textColor}
              >
                (Socket Diameter)
              </text>
            </g>
          )}

          {/* Lt (pileLength) from cap underside to tip/interface */}
          {Lt_m !== null && Lt_m > 0 && (
            <g>
              <line
                x1={dimLtX}
                y1={dimLtTopY + 3}
                x2={dimLtX}
                y2={dimLtBottomY - 5}
                stroke={lineColor}
                strokeWidth="1.2"
                markerStart="url(#arrowStart)"
                markerEnd="url(#arrowEnd)"
              />
              <g
                transform={`translate(${dimLtX + (dimLtX > VW - margin - 40 ? -60 : 8)}, ${(dimLtTopY + dimLtBottomY) / 2 - 6})`}
              >
                <text fontSize="14" fill={textColor}>{`L = ${fmt(Lt_m, 2)} m`}</text>
                <text y={16} fontSize="12" fill={textColor}>
                  (Total pile length)
                </text>
              </g>
            </g>
          )}

          {/* Rock Properties (c0, RQD) + (optional) Nms, qsr */}
          {(c0 || rqd) && (
            <>
              <g role="group" aria-label="Rock Properties panel">
                <rect
                  x={rockBox.x}
                  y={rockBox.y}
                  width={rockBox.w}
                  height={rockBox.h}
                  rx="8"
                  fill={panelFill}
                  stroke={panelStroke}
                />
                <text x={rockBox.x + 10} y={rockBox.y + 18} fontSize={panelHeaderFontSize} fontWeight={600} fill={textColor}>
                  Rock Properties
                </text>
                <text x={rockBox.x + 10} y={rockBox.y + 40} fontSize={panelRowFontSize} fill={textColor}>{`c₀ = ${fmt(
                  c0,
                  1
                )} MPa`}</text>
                <text x={rockBox.x + 10} y={rockBox.y + 62} fontSize={panelRowFontSize} fill={textColor}>{`RQD = ${fmt(
                  rqd,
                  0
                )} %`}</text>
                <text x={rockBox.x + 10} y={rockBox.y + 84} fontSize={panelRowFontSize} fill={textColor}>
                  N
                  <tspan fontSize="9" baselineShift="sub">
                    ms
                  </tspan>{' '}
                  = {fmt(nms, 0)}
                </text>
              </g>
            </>
          )}

          {/* Pile Concrete (γ_conc, f'c) */}
          {(gammaConcrete || sigmaConcrete) && (
            <>
              <g role="group" aria-label="Pile Concrete panel">
                <rect
                  x={concBox.x}
                  y={concBox.y}
                  width={concBox.w}
                  height={concBox.h}
                  rx="8"
                  fill={panelFill}
                  stroke={panelStroke}
                />
                <text x={concBox.x + 10} y={concBox.y + 18} fontSize={panelHeaderFontSize} fontWeight={600} fill={textColor}>
                  Pile Concrete
                </text>
                <text x={concBox.x + 10} y={concBox.y + 40} fontSize={panelRowFontSize} fill={textColor}>
                  γ
                  <tspan fontSize="9" baselineShift="sub">
                    c
                  </tspan>{' '}
                  = {fmt(gammaConcrete, 1)} kN/m³
                </text>

                <text x={concBox.x + 10} y={concBox.y + 62} fontSize={panelRowFontSize} fill={textColor}>
                  σ
                  <tspan fontSize="9" baselineShift="sub">
                    c
                  </tspan>{' '}
                  = {fmt(sigmaConcrete, 1)} MPa
                </text>
              </g>
            </>
          )}

          {/* Loads (static, seismic compression, seismic tension) */}
          {(FSt || FSs || FTension) && (
            <>
              <g role="group" aria-label="Loads panel">
                <rect
                  x={loadsBox.x}
                  y={loadsBox.y}
                  width={loadsBox.w}
                  height={loadsBox.h}
                  rx="8"
                  fill={panelFill}
                  stroke={panelStroke}
                />
                <text x={loadsBox.x + 10} y={loadsBox.y + 18} fontSize={panelHeaderFontSize} fontWeight={600} fill={textColor}>
                  Loads
                </text>
                <text x={loadsBox.x + 10} y={loadsBox.y + 40} fontSize={panelRowFontSize} fill={textColor}>{`Static Comp. = ${fmt(
                  FSt,
                  0
                )} kN`}</text>
                <text
                  x={loadsBox.x + 10}
                  y={loadsBox.y + 62}
                  fontSize={panelRowFontSize}
                  fill={textColor}
                >{`Seismic Comp. = ${fmt(FSs, 0)} kN`}</text>
                <text
                  x={loadsBox.x + 10}
                  y={loadsBox.y + 84}
                  fontSize={panelRowFontSize}
                  fill={textColor}
                >{`Seismic Tens. = ${fmt(FTension, 0)} kN`}</text>
              </g>
            </>
          )}

          {/* Factors of Safety (static, seismic) */}
          {(FsSt || FsSs) && (
            <>
              <g role="group" aria-label="Factors of Safety panel">
                <rect x={fsBox.x} y={fsBox.y} width={fsBox.w} height={fsBox.h} rx="8" fill={panelFill} stroke={panelStroke} />
                <text x={fsBox.x + 10} y={fsBox.y + 18} fontSize={panelHeaderFontSize} fontWeight={600} fill={textColor}>
                  Factors of Safety
                </text>
                <text x={fsBox.x + 10} y={fsBox.y + 40} fontSize={panelRowFontSize} fill={textColor}>{`FS(static) = ${fmt(
                  FsSt,
                  2
                )}`}</text>
                <text x={fsBox.x + 10} y={fsBox.y + 62} fontSize={panelRowFontSize} fill={textColor}>{`FS(seismic) = ${fmt(
                  FsSs,
                  2
                )}`}</text>
              </g>
            </>
          )}

          {/* Empty-state note */}
          {hasCore && (
            <g opacity="0.9">
              <rect x={VW / 2 - 210} y={groundY + 100} width="420" height="60" rx="8" fill={concreteFillColor} stroke="#aaa" />
              <text x={VW / 2} y={groundY + 135} textAnchor="middle" fontSize="14" fill={textColor}>
                Enter parameters to generate the sketch
              </text>
            </g>
          )}
          <text x={VW / 2} y={bottomY - 4} textAnchor="middle" fontSize="11" fill={textColor} opacity="0.8">
            Note: The calculation is for the bearing capacity of a single pile. Two piles are illustrated in the sketch for
            clarity of presentation.
          </text>
        </svg>
      </div>
    </div>
  );
}
